import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { termsToHtml } from "../lib/terms";
import { formatMoney, currency as curMeta } from "../lib/money";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (s) => s ? String(s).slice(0, 10).split("-").reverse().join("-") : "";
const qtyStr = (q) => Number.isInteger(Number(q)) ? String(Number(q)) : String(q);
const daysUntil = (s) => { if (!s) return null; const d = new Date(String(s).slice(0, 10)); const t = new Date(); d.setHours(0, 0, 0, 0); t.setHours(0, 0, 0, 0); return Math.round((d - t) / 86400000); };

const DECLINE_REASONS = ["Prefer not to say", "Price is higher than expected", "Not the right time", "Missing something we need", "Going with another system", "Staying with our current process"];

const Money = ({ children }) => <span className="mono" style={{ whiteSpace: "nowrap" }}>{children}</span>;

function ExpiryChip({ valid }) {
  const d = daysUntil(valid);
  if (d == null) return null;
  if (d <= 0) return <span className="chip" style={{ background: "#FFE9E0", border: "1px solid #F3BFA8", color: "#9A3200" }}><i style={{ background: "#C23A00" }} />Expired</span>;
  const overdue = d <= 7;
  return <span className="chip" style={overdue ? { background: "#FFE9E0", border: "1px solid #F3BFA8", color: "#9A3200" } : { background: "#F1EEE6", border: "1px solid #DAD5CA", color: "#59554D" }}><i style={{ background: overdue ? "#C23A00" : "#8A857A" }} />{d} day{d !== 1 ? "s" : ""} left</span>;
}

function Block({ label, rows, totals, recurring, cur = "AUD", taxLabel = "GST 10%", taxRate = 0.1 }) {
  const suffix = recurring ? " /mo" : "";
  const money = (v) => formatMoney(v, cur);
  const hasOpt = rows.some((r) => r.optional);
  const optTotal = rows.filter((r) => r.optional).reduce((s, r) => s + (r.qty || 0) * (r.unit_price || 0), 0);
  return (
    <div>
      <div className="band"><span className="blabel">{label}</span></div>
      <div className="band colhead"><span className="blabel" style={{ flex: "1 1 200px" }}>DESCRIPTION</span><span className="mgrp mhead"><span>QTY</span><span>UNIT</span><span>AMOUNT</span></span></div>
      {rows.map((r, i) => (
        <div className="row" key={i}>
          <div style={{ flex: "1 1 200px", minWidth: 0 }}>
            <span className="desc">{r.description}</span>
            {r.optional && <span className="optchip">OPTIONAL — ACCEPT OR DECLINE</span>}
          </div>
          <span className="mgrp"><Money>{qtyStr(r.qty)}</Money><Money>{money(r.unit_price)}</Money><Money>{money((r.qty || 0) * (r.unit_price || 0))}{suffix}</Money></span>
        </div>
      ))}
      <div className="totals">
        <div className="trow"><span>Subtotal</span><Money>{money(totals.subtotal)}{suffix}</Money></div>
        <div className="trow"><span>{taxLabel}</span><Money>{money(totals.gst)}{suffix}</Money></div>
        <div className="trow tgrand"><span>{taxRate ? "Total incl GST" : "Total"}</span><Money>{money(totals.total)}{suffix}</Money></div>
        {hasOpt && <div className="trow" style={{ color: "#1D4ED8", fontWeight: 600 }}><span>If optional lines accepted</span><Money>{money((totals.subtotal + optTotal) * (1 + taxRate))}{suffix}</Money></div>}
      </div>
    </div>
  );
}

export default function QuoteView() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [state, setState] = useState("loading"); // loading | view | accept | decline | error
  const [termsOpen, setTermsOpen] = useState(false);
  const [name, setName] = useState("");
  const [position, setPosition] = useState("");
  const [auth, setAuth] = useState(false);
  const [touched, setTouched] = useState(false);
  const [reason, setReason] = useState(DECLINE_REASONS[0]);
  const cur = (q?.currency || "AUD").toUpperCase();
  const isExport = (q?.tax_treatment || (cur === "AUD" ? "domestic" : "export")) === "export";
  const taxRateN = isExport ? 0 : 0.1;
  const taxLabelS = isExport ? "No Tax Applicable" : "GST 10%";
  const money = (v) => formatMoney(v, cur);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errCode, setErrCode] = useState("");

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/quotes/public/${token}`); setQ(r.data.quote); setState("view"); }
    catch (e) { setState("error"); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async (action) => {
    setSubmitting(true); setErrCode("");
    try {
      const r = await axios.post(`${API}/quotes/public/${token}/respond`, {
        action, name: name || null, position: position || null, accepted: auth, comment: action === "decline" ? [reason !== "Prefer not to say" ? reason : "", note].filter(Boolean).join(" — ") || null : (note || null),
      });
      setQ(r.data.quote); setState("view"); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { setErrCode((e?.response?.status || "ERR") + "-" + token.slice(0, 6)); }
    finally { setSubmitting(false); }
  };

  const css = `
    .qv-page{ background:#EEEBE4; min-height:100vh; padding:0 16px 40px; -webkit-text-size-adjust:100%; }
    .qv-wrap{ max-width:800px; margin:0 auto; display:flex; flex-direction:column; gap:14px; padding-top:20px; }
    .mono{ font-family:'JetBrains Mono',ui-monospace,monospace; }
    .qv-mast{ background:#0C100F; border-radius:13px; padding:14px 18px; display:flex; align-items:center; justify-content:space-between; }
    .qv-logo{ font-family:Manrope,sans-serif; font-weight:800; color:#fff; font-size:22px; letter-spacing:-.01em; }
    .qv-logo small{ display:block; font-weight:500; font-size:9px; letter-spacing:.02em; color:#B9BDBB; }
    .qv-dl{ color:#FED001; font-weight:700; font-size:14px; text-decoration:none; display:inline-flex; align-items:center; gap:6px; }
    .card{ background:#fff; border:1px solid #DAD5CA; border-radius:14px; overflow:hidden; box-shadow:0 1px 2px rgba(20,18,14,.05); }
    .qhead{ display:flex; flex-wrap:wrap; gap:12px; justify-content:space-between; padding:20px; border-bottom:1px solid #F0EDE6; }
    .flabel{ font-size:10.5px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:#8A857A; }
    .qnum{ font-family:'JetBrains Mono',monospace; font-size:21px; font-weight:700; letter-spacing:-.01em; color:#1A1A1A; margin-top:2px; }
    .prep{ font-size:13px; color:#59554D; margin-top:4px; }
    .qhead .r{ text-align:right; }
    .vdate{ font-family:'JetBrains Mono',monospace; font-size:15px; font-weight:700; color:#1A1A1A; margin:2px 0 6px; }
    .chip{ display:inline-flex; align-items:center; gap:6px; border-radius:999px; padding:3px 10px; font-size:12.5px; font-weight:700; }
    .chip i{ width:6px; height:6px; border-radius:999px; display:inline-block; }
    .band{ background:#F1EEE6; padding:11px 20px; display:flex; align-items:baseline; }
    .colhead{ border-bottom:1px solid #DAD5CA; padding-top:8px; padding-bottom:8px; }
    .blabel{ font-size:10.5px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color:#8A857A; }
    .row{ display:flex; flex-wrap:wrap; column-gap:12px; row-gap:7px; align-items:baseline; padding:13px 20px; border-bottom:1px solid #F5F2EC; }
    .desc{ font-size:15px; font-weight:600; color:#1A1A1A; text-wrap:pretty; }
    .optchip{ display:inline-block; margin-left:8px; font-size:9.5px; font-weight:700; letter-spacing:.04em; padding:2px 6px; border-radius:4px; background:#EFF6FF; border:1px solid #BFDBFE; color:#1D4ED8; }
    .mgrp{ flex:0 0 auto; display:grid; grid-template-columns:44px 88px 96px; column-gap:12px; text-align:right; }
    .mgrp .mono,.mhead span{ font-size:13.5px; color:#59554D; }
    .mhead{ }
    .mhead span{ font-size:10px; font-weight:500; text-transform:uppercase; letter-spacing:.06em; color:#8A857A; }
    .mgrp span:nth-child(3){ color:#1A1A1A; }
    .totals{ padding:14px 20px; }
    .trow{ display:flex; justify-content:space-between; font-size:14px; color:#59554D; padding:1px 0; }
    .trow .mono{ font-size:13.5px; }
    .tgrand{ font-size:15px; font-weight:700; color:#1A1A1A; }
    .tgrand .mono{ font-size:16px; font-weight:700; color:#1A1A1A; }
    .terms{ padding:18px 20px; border-top:1px solid #F0EDE6; }
    .terms p{ font-size:14px; line-height:1.6; color:#59554D; text-wrap:pretty; margin:0 0 12px; }
    .tbody{ border-top:1px solid #F0EDE6; padding-top:12px; margin-top:4px; }
    .tbody h3{ font-family:Manrope,sans-serif; font-size:15px; font-weight:800; color:#1A1A1A; margin:16px 0 6px; }
    .tbody h4{ font-family:Manrope,sans-serif; font-size:13.5px; font-weight:700; color:#1A1A1A; margin:14px 0 4px; }
    .tbody p{ font-size:14px; line-height:1.65; color:#59554D; margin:0 0 10px; }
    .tbody ul{ margin:0 0 10px; padding-left:20px; }
    .tbody li{ font-size:14px; line-height:1.6; color:#59554D; margin:2px 0; }
    .tbody strong{ color:#1A1A1A; font-weight:700; }
    .link{ background:none; border:0; color:#6B5600; font-size:14px; font-weight:600; cursor:pointer; padding:10px 0; min-height:44px; }
    .aband{ background:#FBFAF7; padding:18px 20px; border-top:1px solid #F0EDE6; }
    .btn-primary{ min-height:56px; padding:0 22px; background:#FFD108; border:1px solid #E0B800; border-radius:12px; font-family:Manrope,sans-serif; font-weight:800; font-size:16px; color:#1A1A1A; cursor:pointer; display:inline-flex; align-items:center; gap:8px; justify-content:center; }
    .btn-primary:active{ transform:translateY(1px); }
    .btn-primary[disabled]{ background:#E6E2D9; border-color:#D3CEC2; color:#9A958B; cursor:default; }
    .btn-secondary{ min-height:52px; padding:0 20px; background:#fff; border:1.5px solid #1A1A1A; border-radius:12px; font-family:Manrope,sans-serif; font-weight:800; font-size:15px; color:#1A1A1A; cursor:pointer; display:inline-flex; align-items:center; gap:8px; justify-content:center; }
    .btn-text{ min-height:44px; background:none; border:0; color:#6B5600; font-size:14px; font-weight:600; cursor:pointer; padding:0 10px; }
    .conseq{ font-size:13px; line-height:1.6; color:#59554D; margin-top:12px; }
    .qhead2{ font-family:Manrope,sans-serif; font-weight:700; font-size:20px; color:#1A1A1A; }
    .field-label{ font-size:13px; font-weight:700; color:#1A1A1A; margin:14px 0 5px; }
    .field{ width:100%; min-height:54px; padding:0 15px; border-radius:12px; border:1.5px solid #C9C3B6; font-size:16px; color:#1A1A1A; outline:none; background:#fff; box-sizing:border-box; }
    .field.err{ border-color:#C23A00; }
    .field-err{ font-size:13px; font-weight:600; color:#9A3200; margin-top:5px; }
    .helper{ font-size:13px; color:#8A857A; margin-top:5px; }
    .authrow{ min-height:52px; display:flex; align-items:center; gap:12px; cursor:pointer; margin-top:14px; }
    .authrow .box{ width:24px; height:24px; border-radius:6px; border:1.5px solid #C9C3B6; background:#fff; display:flex; align-items:center; justify-content:center; flex:0 0 auto; }
    .authrow .box.on{ background:#FFD108; border-color:#E0B800; }
    .authrow span{ font-size:14px; line-height:1.55; color:#1A1A1A; }
    .contact{ padding:20px; display:flex; flex-wrap:wrap; gap:12px; align-items:center; justify-content:space-between; }
    .footer{ font-size:13px; color:#59554D; line-height:1.7; padding:0 4px; white-space:pre-wrap; }
    .succ{ padding:20px; }
    .whatcard{ background:#fff; border:1px solid #A9D9BE; border-radius:12px; padding:14px 16px; margin-top:12px; }
    @media (max-width:520px){ .mgrp{ grid-template-columns:40px 78px 84px; } }
  `;

  useEffect(() => {
    const el = document.createElement("style");
    el.id = "qv-inline-styles";
    el.textContent = css;
    document.head.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (state === "loading") return (<div className="qv-page"><style>{css}</style><div className="qv-wrap"><div className="qv-mast"><span className="qv-logo">EQUIPR<small>Plan Better. Work Better.</small></span></div><div className="card"><div className="qhead"><div><div className="flabel">Quote</div><div className="skeleton" style={{ height: 24, width: 200, marginTop: 6 }} /></div></div><div style={{ padding: 20 }}><div className="skeleton" style={{ height: 16, width: "60%" }} /></div></div></div></div>);

  if (state === "error") return (<div className="qv-page" data-testid="qv-invalid"><style>{css}</style><div className="qv-wrap"><div className="qv-mast"><span className="qv-logo">EQUIPR<small>Plan Better. Work Better.</small></span></div><div className="card"><div style={{ padding: 24 }}><p style={{ fontSize: 15, color: "#1A1A1A", fontWeight: 600 }}>This quote link is no longer valid.</p><p style={{ fontSize: 14, color: "#59554D", marginTop: 8 }}>Please contact the person who sent it to you for an updated link.</p></div></div></div></div>);

  const pdfUrl = `${API}/quotes/public/${token}/pdf`;
  const oneOff = (q.line_items || []).filter((l) => (l.billing || "one_off") !== "monthly");
  const monthly = (q.line_items || []).filter((l) => (l.billing || "one_off") === "monthly");
  const monthlyTotal = q.totals?.monthly?.total || 0;
  const oneOffTotal = q.totals?.one_off?.total || 0;
  const expired = q.status === "expired" || (daysUntil(q.valid_until) != null && daysUntil(q.valid_until) <= 0 && q.status === "sent");
  const nameErr = touched && name.trim().length < 2;
  const posErr = touched && position.trim().length < 2;
  const ready = name.trim().length >= 2 && position.trim().length >= 2 && auth;
  const owner = q.owner_name || q.from_name || "your EQUIPR contact";
  const mailto = `mailto:${q.support_email || ""}?subject=${encodeURIComponent(`Question about quote ${q.quote_number}`)}`;

  return (
    <div className="qv-page"><style>{css}</style>
      <div className="qv-wrap" data-testid="quote-public-view">
        {/* Masthead */}
        <div className="qv-mast">
          <span className="qv-logo">EQUIPR<small>Plan Better. Work Better.</small></span>
          <a className="qv-dl" href={pdfUrl} target="_blank" rel="noreferrer" data-testid="qv-download">⤓ Download PDF</a>
        </div>

        {/* Quote card */}
        <div className="card">
          <div className="qhead">
            <div>
              <div className="flabel">Quote</div>
              <div className="qnum">{q.quote_number}</div>
              <div className="prep">Prepared for <strong>{q.link_name}</strong>{q.contact_name ? ` · ${q.contact_name}` : ""}</div>
            </div>
            <div className="r">
              <div className="flabel">Valid until</div>
              <div className="vdate">{fmt(q.valid_until)}</div>
              <ExpiryChip valid={q.valid_until} />
            </div>
          </div>

          {oneOff.length > 0 && <Block label="One-off" rows={oneOff} totals={q.totals?.one_off || {}} cur={cur} taxLabel={taxLabelS} taxRate={taxRateN} />}
          {monthly.length > 0 && <Block label="Per month" rows={monthly} totals={q.totals?.monthly || {}} recurring cur={cur} taxLabel={taxLabelS} taxRate={taxRateN} />}

          {/* Terms */}
          <div className="terms">
            <div className="flabel" style={{ marginBottom: 6 }}>Terms and conditions</div>
            <p>{isExport
              ? `Unit prices are shown in ${curMeta(cur).name}. No tax is applicable to this quote — as an Australian registered company, supplies to overseas customers are treated as tax-free exports. This quote is valid until the date shown.`
              : `Unit prices are shown in ${curMeta(cur).name} excluding GST. Subtotals, GST and totals are shown separately above, and the totals include GST at 10%. This quote is valid until the date shown.`}</p>
            {q.terms && <>
              <button className="link" onClick={() => setTermsOpen((v) => !v)} data-testid="qv-terms">{termsOpen ? "Hide the full terms" : "Read the full terms"}</button>
              {termsOpen && <div className="tbody" data-testid="qv-terms-body" dangerouslySetInnerHTML={{ __html: termsToHtml(q.terms) }} />}
            </>}
          </div>

          {/* Action band — state machine */}
          {q.status === "accepted" ? (
            <div className="succ" style={{ background: "#E3F3E9", borderTop: "1px solid #A9D9BE" }} data-testid="qv-accepted">
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 22, height: 22, borderRadius: 999, background: "#0E6B3F", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13 }}>✓</span>
                <span style={{ fontFamily: "Manrope,sans-serif", fontWeight: 700, fontSize: 20, color: "#0B4F2E" }}>Quote accepted</span>
              </div>
              <p style={{ fontSize: 14, color: "#0B4F2E", marginTop: 8 }}>Recorded for <strong>{q.responded_by}</strong>{q.responded_position ? `, ${q.responded_position}` : ""}, on <span className="mono">{fmt(q.accepted_at)}</span>.</p>
              <div className="whatcard">
                <div className="flabel" style={{ marginBottom: 6 }}>What you accepted</div>
                {monthlyTotal > 0 && <div><span className="mono" style={{ fontSize: 17, fontWeight: 700 }}>{money(monthlyTotal)}</span> <span style={{ fontSize: 13, color: "#59554D" }}>per month</span></div>}
                {oneOffTotal > 0 && <div><span className="mono" style={{ fontSize: 17, fontWeight: 700 }}>{money(oneOffTotal)}</span> <span style={{ fontSize: 13, color: "#59554D" }}>one-off</span></div>}
                <div style={{ fontSize: 12, color: "#8A857A", marginTop: 4 }}>{isExport ? `Amounts in ${cur} — no tax applicable.` : "Both figures include GST."} Reference <span className="mono">{q.quote_number}</span></div>
              </div>
              <p style={{ fontSize: 14, color: "#0B4F2E", marginTop: 12 }}>A copy has been emailed to you. {owner} will be in touch shortly to start your onboarding.</p>
              <a className="btn-secondary" href={pdfUrl} target="_blank" rel="noreferrer" style={{ marginTop: 12, textDecoration: "none" }}>Download your accepted quote</a>
            </div>
          ) : q.status === "declined" ? (
            <div className="succ" style={{ background: "#FFE9E0", borderTop: "1px solid #F3BFA8" }} data-testid="qv-declined">
              <span style={{ fontFamily: "Manrope,sans-serif", fontWeight: 700, fontSize: 20, color: "#9A3200" }}>Quote declined</span>
              <p style={{ fontSize: 14, color: "#9A3200", marginTop: 8 }}>Nothing has been charged and nothing has started. {owner} has been notified. If you change your mind, the quote stays valid until <span className="mono">{fmt(q.valid_until)}</span> and you can accept it from this page.</p>
              <button className="btn-text" onClick={() => { setState("view"); }} data-testid="qv-back-to-quote" style={{ marginTop: 8 }}>Back to the quote — accept instead</button>
              {state === "accept" && renderAccept()}
            </div>
          ) : q.status === "void" ? (
            <div className="aband" data-testid="qv-void"><p style={{ fontSize: 14, color: "#59554D" }}>This quote has been replaced. Please contact {owner} for the current version — you may have earlier figures in an email to reconcile.</p></div>
          ) : expired ? (
            <div className="aband" data-testid="qv-expired">
              <p style={{ fontSize: 14, color: "#59554D", marginBottom: 12 }}>This quote expired on <span className="mono">{fmt(q.valid_until)}</span>.</p>
              <a className="btn-secondary" href={mailto} style={{ textDecoration: "none" }}>Ask for an updated quote</a>
            </div>
          ) : state === "accept" ? renderAccept()
            : state === "decline" ? (
              <div className="aband" data-testid="qv-decline-form">
                <div className="qhead2">Decline this quote</div>
                <p className="conseq" style={{ marginTop: 6 }}>Nothing is charged and nothing starts. Telling us why is optional, but it helps us quote better next time.</p>
                <div className="field-label">Reason</div>
                <select className="field" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="qv-decline-reason">{DECLINE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}</select>
                <div className="field-label">Anything else (optional)</div>
                <textarea className="field" style={{ minHeight: 84, padding: "12px 15px" }} value={note} onChange={(e) => setNote(e.target.value)} data-testid="qv-decline-note" />
                {errCode && <p className="field-err">Something went wrong sending that. <button className="link" style={{ padding: 0 }} onClick={() => submit("decline")}>Retry</button> · <span className="mono" style={{ fontSize: 11 }}>{errCode}</span></p>}
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
                  <button className="btn-secondary" disabled={submitting} onClick={() => submit("decline")} data-testid="qv-send-decline">Send decline</button>
                  <button className="btn-text" onClick={() => setState("view")}>Back</button>
                </div>
              </div>
            ) : (
              <div className="aband" data-testid="qv-view-actions">
                <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <button className="btn-primary" onClick={() => { setState("accept"); setTouched(false); }} data-testid="qv-accept">✓ Accept this quote</button>
                  <button className="btn-text" onClick={() => setState("decline")} data-testid="qv-decline">Decline</button>
                </div>
                <p className="conseq">Accepting records your name and position against this quote and starts your onboarding. You will be asked to confirm before anything is recorded.</p>
              </div>
            )}
        </div>

        {/* Contact card */}
        <div className="card">
          <div className="contact">
            <div>
              <div className="flabel">Questions about this quote</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#1A1A1A", marginTop: 4 }}>{owner}</div>
              {(q.support_email || q.support_phone) && <div style={{ fontSize: 13, color: "#59554D" }}>{[q.support_email, q.support_phone].filter(Boolean).join(" · ")}</div>}
            </div>
            <a className="btn-secondary" href={mailto} style={{ minHeight: 48, textDecoration: "none" }} data-testid="qv-ask">✉ Ask a question</a>
          </div>
        </div>

        {/* Issuer footer */}
        <div className="footer">{q.company_details || q.from_name}{q.created_at ? `\nQuote dated ${fmt(q.created_at)}` : ""}</div>
      </div>
    </div>
  );

  function renderAccept() {
    return (
      <div className="aband" data-testid="qv-accept-form">
        <div className="qhead2">Accept this quote</div>
        <p className="conseq" style={{ marginTop: 6 }}>You are accepting <strong>{money(monthlyTotal)} per month</strong>{oneOffTotal > 0 ? <> plus <strong>{money(oneOffTotal)} one-off</strong></> : null}, {isExport ? ` in ${cur} (no tax applicable)` : ", both including GST"} against quote <span className="mono">{q.quote_number}</span>.</p>

        <div className="field-label">Your full name</div>
        <input className={`field${nameErr ? " err" : ""}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Doe" data-testid="qv-name" />
        {nameErr && <p className="field-err">Enter the name of the person accepting.</p>}

        <div className="field-label">Your position</div>
        <input className={`field${posErr ? " err" : ""}`} value={position} onChange={(e) => setPosition(e.target.value)} placeholder="e.g. Operations Manager" data-testid="qv-position" />
        {posErr ? <p className="field-err">Enter your position, for example Director or Operations Manager.</p> : <p className="helper">Recorded alongside your name so we know who authorised the agreement.</p>}

        <div className="authrow" onClick={() => setAuth((v) => !v)} data-testid="qv-auth">
          <span className={`box${auth ? " on" : ""}`}>{auth && <span style={{ fontSize: 14, color: "#1A1A1A" }}>✓</span>}</span>
          <span>I confirm I am authorised to accept this quote on behalf of {q.link_name}, and I agree to the terms above.</span>
        </div>

        {errCode && <p className="field-err">Something went wrong. Your details are kept — <button className="link" style={{ padding: 0 }} onClick={() => submit("accept")}>retry</button> · <span className="mono" style={{ fontSize: 11 }}>{errCode}</span></p>}

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
          <button className="btn-primary" disabled={!ready || submitting} title={!ready ? "Enter your name, position and tick the authorisation" : ""} onClick={() => { setTouched(true); if (ready) submit("accept"); }} data-testid="qv-confirm">Confirm acceptance</button>
          <button className="btn-text" onClick={() => setState("view")}>Back</button>
        </div>
        <p className="conseq">{ready ? <>This records your acceptance against {q.quote_number} with today's date, emails you a copy, and notifies {owner}. It cannot be undone from this page.</> : "Your name, your position and the authorisation tick are all required before this quote can be accepted."}</p>
      </div>
    );
  }
}
